import { useEffect } from "react";
import styled, { css } from "styled-components";
import { Flex, Image, Text } from "rebass/styled-components";
import { useHistory } from "react-router-dom";

import { useRecoilState, useSetRecoilState, useRecoilValue } from "recoil";
import { FaMinus, FaPlus } from "react-icons/fa";
import { ReactComponent as HistoryLogo } from "../../../Assets/Nav/nav-history.svg";
import { LoadingSkeleton } from "../../../Components/Skeloton";
import { Divider } from "../../../Components/Divider";
import { URLS } from "../../../Utilities/Maps";
import { Body, Title3 } from "../../../Components/Text";
import { Button } from "../../../Components/Button";
import { MatchesViewmodel } from "../../../Views/MatchHistory/ViewModel";

import { GameSelector } from "../Components/Shared/GameSelector";
import {
  MatchHistoryGame,
  MatchHistoryWidgetOpen,
  MatchHistoryWidgetIndentifierState,
  MatchHistoryWidgetSourceState,
} from "../../../State/matchHistory";

import { ESupportedGames } from "MVVM/@types";

import { Matches } from "./Matches";
import { LinkingOptions } from "../Components/Shared/NoLinkedAccount/LinkingOptions";
import { NoMoments } from "../Components/Shared/Match/NoMoments";
import { BetaCallout } from "../Components/Shared/BetaCallout";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";
import { useResolvedPlayerIdentifier } from "MVVM/Hooks/useResolvedPlayerIdentifier";
import { useTranslation } from "react-i18next";
import { analytics } from "MVVM/Analytics";

const { REACT_APP_S3_CLIP_BUCKET } = process.env;

const getAccountInfo = (
  matchHistoryGame: number | string,
  playerGameIdentifier?: string,
) => {
  const { hasRiotLinked, hasEpicLinked, hasSteamLinked } = useLinkedAccounts();
  if (playerGameIdentifier) return false;
  return (
    (!hasRiotLinked && matchHistoryGame === ESupportedGames.LOL) ||
    (!hasEpicLinked && matchHistoryGame === ESupportedGames.FORTNITE) ||
    (!hasSteamLinked && matchHistoryGame === ESupportedGames.CS2) ||
    matchHistoryGame === ESupportedGames.NONE
  );
};

export const MatchHistoryWidget = ({
  showDivider,
  playerGameIdentifier,
  pageSize = 2,
  renderPromoSection = true,
  renderCollapseIcon = true,
  renderGameSelector = true,
  width = "100%",
  title = "mhWidget.title",
  defaultTitle = "Your Recent Top Plays",
  subtitle = "mhWidget.subtitle",
  defaultSubtitle = "Here are some of your best moments from recent matches.",
}: {
  showDivider?: boolean;
  playerGameIdentifier?: string;
  pageSize?: number;
  renderPromoSection?: boolean;
  renderCollapseIcon?: boolean;
  renderGameSelector?: boolean;
  width?: string;
  title?: string;
  defaultTitle?: string;
  subtitle?: string;
  defaultSubtitle?: string;
}) => {
  const history = useHistory();
  const [matchHistoryGame] = useRecoilState(MatchHistoryGame);
  const [matchHistoryWidgetOpen, setMatchHistoryWidgetOpen] = useRecoilState(
    MatchHistoryWidgetOpen,
  );
  const setWidgetPlayerIdentifier = useSetRecoilState(
    MatchHistoryWidgetIndentifierState,
  );
  const widgetSource = useRecoilValue(MatchHistoryWidgetSourceState);
  const { resolvedPlayerIdentifier, loading: resolvingPlayerIdentifier } =
    useResolvedPlayerIdentifier(playerGameIdentifier, matchHistoryGame);
  const accountLinkRequired = getAccountInfo(
    matchHistoryGame,
    playerGameIdentifier,
  );
  const { matchesLoading, matches } = MatchesViewmodel(
    pageSize,
    resolvedPlayerIdentifier,
    resolvingPlayerIdentifier,
  );
  const displayNoLinkedAccounts = accountLinkRequired && !matchesLoading;
  const displayNoMatches =
    !matchesLoading && !accountLinkRequired && matches.length === 0;
  const displayMatches =
    !matchesLoading && !accountLinkRequired && matches.length > 0;
  const displayLoadingSkeletons = matchesLoading && !accountLinkRequired;
  const { t } = useTranslation();

  useEffect(() => {
    if (resolvedPlayerIdentifier) {
      setWidgetPlayerIdentifier(resolvedPlayerIdentifier);
    }
  }, [resolvedPlayerIdentifier, setWidgetPlayerIdentifier]);

  useEffect(() => {
    if (widgetSource === "iframe") {
      analytics.page("Top Plays - Impression", {
        widgetSource: widgetSource,
      });
    }
  }, [widgetSource]);

  return (
    <>
      <WidgetContainer $width={width}>
        <WidgetHeader>
          <WidgetTitleContainer>
            <Flex alignItems="center" style={{ gap: 16 }}>
              <Logo />
              <StyledTitle
                variant={"text.headingMedium"}
                color="ice"
                $widgetSource={widgetSource}
              >
                {t(title, defaultTitle)}
              </StyledTitle>
            </Flex>
            {renderCollapseIcon &&
              (matchHistoryWidgetOpen ? (
                <StyledFaMinus
                  onClick={() => setMatchHistoryWidgetOpen(false)}
                />
              ) : (
                <StyledFaPlus onClick={() => setMatchHistoryWidgetOpen(true)} />
              ))}
          </WidgetTitleContainer>
          {matchHistoryWidgetOpen && (
            <>
              <StyledSubtitle
                variant="text.labelMedium"
                color="chalk"
                $widgetSource={widgetSource}
              >
                {accountLinkRequired
                  ? t("mhWidget.linkAccount")
                  : t(subtitle, defaultSubtitle)}
              </StyledSubtitle>
              {renderGameSelector && <GameSelector />}
              {matchHistoryGame === ESupportedGames.FORTNITE && <BetaCallout />}
            </>
          )}
        </WidgetHeader>
        {matchHistoryWidgetOpen && (
          <>
            {(displayNoLinkedAccounts || displayNoMatches) && <Divider />}
            {displayNoLinkedAccounts && <LinkingOptions />}
            {displayLoadingSkeletons && (
              <Flex
                flexDirection={"column"}
                style={{ gap: 20, padding: "15px" }}
              >
                {[1, 1, 1].map((_) => {
                  return <LoadingSkeleton width={"100%"} height={100} />;
                })}
              </Flex>
            )}
            {displayMatches && <Matches matches={matches} />}
            {displayNoMatches && <NoMoments style={{ marginTop: -40 }} />}
            {renderPromoSection && (
              <>
                <Divider />
                <GoToMatchHistoryContainer>
                  <WantToCreateMoreContainer
                    flexDirection={"column"}
                    justifyContent="center"
                    alignItems="start"
                    maxWidth={"300px"}
                  >
                    <StyledTitle3 my={3}>
                      {t("mhWidget.footerTitle")}
                    </StyledTitle3>
                    <StyledBody color="chalk" variant="text.labelMedium">
                      {t("mhWidget.footerSubtitle")}
                    </StyledBody>
                    <StyledButton
                      size="tiny"
                      flex={true}
                      onClick={() => {
                        analytics.track("Match History - Go To Match History", {
                          isWidget: true,
                        });
                        history.push(URLS.MATCH_HISTORY);
                      }}
                    >
                      {t("mhWidget.footerButton")}
                    </StyledButton>
                  </WantToCreateMoreContainer>
                  <StyledImage
                    src={`${REACT_APP_S3_CLIP_BUCKET}/static/web/matchHistory/match_history.webp`}
                  />
                </GoToMatchHistoryContainer>
              </>
            )}
          </>
        )}
      </WidgetContainer>{" "}
      {showDivider && <Divider />}
    </>
  );
};

const StyledTitle = styled(Text)<{ $widgetSource: string }>`
  ${({ $widgetSource }) =>
    $widgetSource === "iframe" &&
    css`
      @media (max-height: 400px), (max-width: 768px) {
        font-size: 22px;
        line-height: 26px;
      }
    `}
`;

const StyledSubtitle = styled(Text)<{ $widgetSource: string }>`
  ${({ $widgetSource }) =>
    $widgetSource === "iframe" &&
    css`
      @media (max-height: 400px), (max-width: 768px) {
        font-size: 14px;
        line-height: 16px;
      }
    `}
`;

const StyledFaPlus = styled(FaPlus)`
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  height: 24px;
  justify-self: flex-end;
  width: 24px;
`;

const StyledFaMinus = styled(FaMinus)`
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  height: 24px;
  justify-self: flex-end;
  width: 24px;
`;
const WantToCreateMoreContainer = styled(Flex)`
  align-items: start;
  flex-direction: column;
  justify-content: center;
  max-width: 300px;
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    align-items: center;
    justify-content: center;
    max-width: unset;
  }
`;
const GoToMatchHistoryContainer = styled(Flex)`
  align-items: center;
  justify-content: space-evenly;
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    flex-direction: column-reverse;
    justify-content: start;

    gap: 20px;
  }
`;

const StyledImage = styled(Image)`
  border-radius: 4px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.4);
  max-width: 400px;
  width: 100%;
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    max-width: unset;
  }
`;
const StyledBody = styled(Body)`
  max-width: 540px;
  text-align: start;
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    text-align: center;
  }
`;

const StyledTitle3 = styled(Title3)`
  font-weight: 600;
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    margin-top: 0px;
  }
`;
const StyledButton = styled(Button)`
  border-radius: 24px;
  height: 30px;
  margin-top: 20px;
  padding: 0px 20px;
`;

const Logo = styled(HistoryLogo)`
  fill: ${(props) => props.theme.colors.envy};
  height: 36px;
  width: 36px;
`;

const WidgetContainer = styled(Flex)<{ $width: string }>`
  background-color: ${({ theme }) => theme.colors.darkerNed};
  border-color: #233438;
  border-radius: 16px;
  border-style: solid;
  border-width: 1px;
  box-shadow: 0 0 30px #233438;
  flex-direction: column;
  gap: 24px;
  mix-blend-mode: normal;
  padding: 24px;
  width: ${(props) => props.$width};
  pointer-events: auto;
`;

const WidgetHeader = styled(Flex)`
  align-items: start;
  flex-direction: column;
  gap: 16px;
`;

const WidgetTitleContainer = styled(Flex)`
  align-items: center;
  flex-direction: row;
  gap: 16px;
  justify-content: space-between;
  width: 100%;
`;
